/* eslint-disable @typescript-eslint/naming-convention */
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Slide, Typography } from '@mui/material';
import userLockLogo from 'assets/Images/Auth/lock.png';
import PasswordField from 'components/Common/LabelledTextField/PasswordField';
import { getLocalStorageItem } from 'hooks';
import { useCreateNewPasswordQuery } from 'queries/AuthQueries';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HandleErrorResponse from 'utils/HandleErrorResponse';
import * as yup from 'yup';

import AuthLayout from '../../components/Layout/AuthLayout';

// const { password, confirmPassword } = FormValidationSchemas;

const translatedUserConfirmPasswordSchema = (t: Function) => {
  const passwordText = t('password');
  const confirmPasswordText = t('confirmPassword');
  const userConfirmPasswordSchema = yup.object().shape({
    new_password: yup
      .string()
      .nullable()
      .required(() => t('requiredErrorMessage', { field: passwordText }))
      .min(8, () => t('minErrorMessage', { field: passwordText, minValue: 8 }))
      .max(32, () =>
        t('maxErrorMessage', { field: passwordText, maxValue: 32 })
      )
      .matches(/^\S*$/, () => t('whiteSpaceErrorMessage'))
      .matches(/([a-z])/, {
        message: t('atleastOneSmallLetter'),
        name: 'small letter',
        excludeEmptyString: true,
      })
      // 'Atleast one small letter'
      .matches(/[A-Z]/, () => t('atleastOneCapitalLetter'))
      .matches(/(\d)/, () => t('atleastOneNumber'))
      .matches(/(\W)/, () => t('oneSpecialCharacter')),

    confirm_password: yup
      .string()
      .nullable()
      .required(() => t('requiredErrorMessage', { field: confirmPasswordText }))
      .matches(/^\S*$/, () => t('whiteSpaceErrorMessage'))
      // eslint-disable-next-line consistent-return
      .when('new_password', (new_password, schema): any => {
        if (new_password)
          return schema
            .required('Required')
            .test(
              'Match password',
              `${t('doesNotMatchError', { field: passwordText })}`,
              (confirmPasswordValue: string | null, formValues: any) => {
                return confirmPasswordValue === formValues.parent.new_password;
              }
            );
      }),
  });

  return userConfirmPasswordSchema;
};

const ConfirmPassword = () => {
  const { t } = useTranslation('confirmPassword');
  const { t: translate, i18n } = useTranslation('validationErrorMessages');
  const [otp, setOtp] = useState<any>(0);
  const { mutate: setNewPasswordApi } = useCreateNewPasswordQuery();
  const userConfirmPasswordSchema = useMemo(() => {
    return translatedUserConfirmPasswordSchema(translate);
  }, [translate, i18n.language]);
  const {
    register,
    handleSubmit,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userConfirmPasswordSchema),
  });
  const navigate = useNavigate();
  // handlers
  const submitAuthForm = (formData: any) => {
    const creds = getLocalStorageItem('forgotCreds');
    const payload: any = {
      code: creds?.code,
      username: creds?.sendOn,
      password: formData?.new_password,
      confirmPassword: formData?.confirm_password,
    };
    setNewPasswordApi(payload, {
      onSuccess: () => {
        navigate('/login');
      },
      onError: (err: any) => {
        HandleErrorResponse(err, setError);
      },
    });
  };
  const [new_password, confirm_password] = watch([
    'new_password',
    'confirm_password',
  ]);
  useEffect(() => {
    if (confirm_password) {
      trigger('confirm_password');
    }
  }, [new_password, trigger]);
  const handleAuthErrors = (err: any) => {
    console.log(err, 'errors');
  };

  return (
    <AuthLayout>
      <>
        <Slide direction={'right'} in timeout={600}>
          <div className="mx-4 w-[532px] rounded-[12px] bg-[#FFFFFF] p-[32px]">
            <div className="mx-auto mb-4 text-center">
              <img src={userLockLogo} alt="" className="mb-3" />
              <Typography
                fontWeight={600}
                variant="h5"
                color="secondary.100"
                align="center"
              >
                {t('enterNewPassword')}{' '}
              </Typography>
              <Typography
                fontWeight={400}
                variant="subtitle1"
                color="font.900"
                align="center"
                paddingTop={2}
              >
                {t('enterPasswordAndConfirm')}
              </Typography>
            </div>

            <form
              id="login-form"
              onSubmit={handleSubmit(submitAuthForm, handleAuthErrors)}
            >
              <Grid item container spacing={4} xs={12} paddingTop={2}>
                <Grid item xs={12}>
                  <PasswordField
                    label={`${t('newPassword')}`}
                    placeholder={`${t('enterPassword')}`}
                    {...register('new_password')}
                    error={Boolean(errors.new_password)}
                    helperText={
                      errors &&
                      errors.new_password &&
                      errors.new_password.message
                    }
                    className="text-sm font-medium text-secondary-200"
                  />
                </Grid>
                <Grid item xs={12}>
                  <PasswordField
                    label={`${t('confirmPassword')}`}
                    placeholder={`${t('enterConfirmPassword')}`}
                    {...register('confirm_password')}
                    error={Boolean(errors.confirm_password)}
                    helperText={
                      errors &&
                      errors.confirm_password &&
                      errors.confirm_password.message
                    }
                    className="text-sm font-medium text-secondary-200"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    id="login-form"
                    fullWidth
                    type="submit"
                    className="!bg-[#11AC9E] !text-white"
                    sx={{
                      height: '44px',
                    }}
                  >
                    {t('changePasswordButton')}{' '}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </div>
        </Slide>
      </>
    </AuthLayout>
  );
};

export default ConfirmPassword;
